// src/components/RevenueListScreen.jsx
// Lista de receitas premium: GET/DELETE, busca, filtros, métricas e edição.
// Edit abre /finance/revenue/edit com state.revenue.
// Padrões: mostra erro real do backend e atualiza token via x-new-token.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/revenueList.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const CATEGORY_META = {
  Consulta: {
    icon: "🧠",
    tone: "green",
  },
  Palestra: {
    icon: "🎤",
    tone: "blue",
  },
  Workshop: {
    icon: "🧩",
    tone: "violet",
  },
  Outro: {
    icon: "✨",
    tone: "amber",
  },
};

const SORT_OPTIONS = [
  { value: "date-desc", label: "Mais recentes" },
  { value: "date-asc", label: "Mais antigas" },
  { value: "value-desc", label: "Maior valor" },
  { value: "value-asc", label: "Menor valor" },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function pad2(value) {
  return String(value).padStart(2, "0");
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function parseMoney(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function moneyBR(value) {
  const number = parseMoney(value);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function parseDate(value) {
  if (!value) return null;

  const raw = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "-";

  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatMonthBR(value) {
  const date = parseDate(value);

  if (!date) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function isCurrentMonth(value) {
  const date = parseDate(value);
  const now = new Date();

  if (!date) return false;

  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
}

function getRevenueId(item) {
  return item?._id || item?.id || "";
}

function getRevenueValue(item) {
  return parseMoney(item?.valor ?? item?.value ?? 0);
}

function getRevenueCategory(item) {
  return item?.categoria ?? item?.category ?? "Outro";
}

function getRevenueDate(item) {
  return item?.data ?? item?.date ?? item?.createdAt ?? null;
}

function getRevenueDescription(item) {
  return item?.descricao ?? item?.description ?? "";
}

function getCategoryMeta(category) {
  return CATEGORY_META[category] || CATEGORY_META.Outro;
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function ConfirmModal({ open, revenue, deleting, onClose, onConfirm }) {
  if (!open) return null;

  return (
    <div className="revlist-modal-overlay" role="dialog" aria-modal="true">
      <div className="revlist-modal">
        <div className="revlist-modal__icon">!</div>

        <div className="revlist-modal__body">
          <h2>Confirmar exclusão</h2>
          <p>
            Deseja excluir a receita de{" "}
            <strong>{moneyBR(getRevenueValue(revenue))}</strong>?
          </p>
          <span>Essa ação não poderá ser desfeita.</span>
        </div>

        <div className="revlist-modal__actions">
          <button
            className="revlist-btn revlist-btn--ghost-dark"
            type="button"
            onClick={onClose}
            disabled={deleting}
          >
            Cancelar
          </button>

          <button
            className="revlist-btn revlist-btn--danger"
            type="button"
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? "Excluindo..." : "Excluir"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RevenueSkeleton() {
  return (
    <div className="revlist-skeleton-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, index) => (
        <div className="revlist-skeleton-card" key={`revenue-skeleton-${index}`}>
          <span />
          <div>
            <strong />
            <small />
          </div>
          <em />
        </div>
      ))}
    </div>
  );
}

export default function RevenueListScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [confirmDel, setConfirmDel] = useState({
    open: false,
    revenue: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const refreshSignal = useMemo(
    () => location.state?.refreshRevenues || location.state?.refresh,
    [location.state]
  );

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate]);

  const fetchRevenues = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetch(
          `${API_BASE}/psy/receita?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }

        if (response.status === 404) {
          setItems([]);
          setLastUpdatedAt(new Date());
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          toast.error(message);
          setItems([]);
          return;
        }

        const data = await response.json();
        setItems(Array.isArray(data) ? data : []);
        setLastUpdatedAt(new Date());
      } catch (error) {
        console.error("Erro ao buscar receitas:", error);
        toast.error("Falha de comunicação com a API.");
        setItems([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, navigate]
  );

  useEffect(() => {
    fetchRevenues();
  }, [fetchRevenues]);

  useEffect(() => {
    if (refreshSignal) {
      fetchRevenues({ silent: true });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [refreshSignal, fetchRevenues, navigate, location.pathname]);

  const filteredItems = useMemo(() => {
    const q = normalizeText(query);

    const filtered = (items || []).filter((item) => {
      if (!q) return true;

      const haystack = normalizeText(
        [
          getRevenueCategory(item),
          getRevenueDescription(item),
          moneyBR(getRevenueValue(item)),
          formatDateBR(getRevenueDate(item)),
          formatMonthBR(getRevenueDate(item)),
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(q);
    });

    return filtered.sort((a, b) => {
      const valueA = getRevenueValue(a);
      const valueB = getRevenueValue(b);
      const dateA = parseDate(getRevenueDate(a))?.getTime() || 0;
      const dateB = parseDate(getRevenueDate(b))?.getTime() || 0;

      if (sortBy === "date-asc") return dateA - dateB;
      if (sortBy === "value-desc") return valueB - valueA;
      if (sortBy === "value-asc") return valueA - valueB;

      return dateB - dateA;
    });
  }, [items, query, sortBy]);

  const metrics = useMemo(() => {
    const total = items.reduce((sum, item) => sum + getRevenueValue(item), 0);
    const monthTotal = items
      .filter((item) => isCurrentMonth(getRevenueDate(item)))
      .reduce((sum, item) => sum + getRevenueValue(item), 0);

    const highest = items.reduce((current, item) => {
      if (!current) return item;
      return getRevenueValue(item) > getRevenueValue(current) ? item : current;
    }, null);

    const avg = items.length ? total / items.length : 0;

    return {
      total,
      monthTotal,
      avg,
      highest,
      count: items.length,
    };
  }, [items]);

  const askDelete = (revenue) => {
    const id = getRevenueId(revenue);

    if (!id) {
      toast.error("Receita inválida.");
      return;
    }

    setConfirmDel({ open: true, revenue });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setConfirmDel({ open: false, revenue: null });
  };

  const doDelete = async () => {
    const revenue = confirmDel.revenue;
    const id = getRevenueId(revenue);

    if (!id) {
      setConfirmDel({ open: false, revenue: null });
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setDeleting(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/receita/${encodeURIComponent(id)}?id=${encodeURIComponent(
          auth.userId
        )}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        toast.error("Sessão expirada. Faça login novamente.");
        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok) {
        const message = await readErrorMessage(response);
        toast.error(message);
        return;
      }

      toast.success("Receita excluída com sucesso.");
      setItems((prev) => prev.filter((item) => getRevenueId(item) !== id));
      setConfirmDel({ open: false, revenue: null });
    } catch (error) {
      console.error("Erro ao excluir receita:", error);
      toast.error("Falha de comunicação ao excluir receita.");
    } finally {
      setDeleting(false);
    }
  };

  const goEdit = (revenue) => {
    navigate("/finance/revenue/edit", { state: { revenue } });
  };

  const goNew = () => {
    navigate("/finance/revenue/new");
  };

  return (
    <div className="revlist-page">
      <div className="revlist-bg" aria-hidden="true">
        <span className="revlist-bg__orb revlist-bg__orb--one" />
        <span className="revlist-bg__orb revlist-bg__orb--two" />
        <span className="revlist-bg__orb revlist-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="revlist-topbar">
        <button
          className="revlist-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="revlist-brand">
          <img src={Logo} alt="PsyAssist" className="revlist-brand__logo" />
          <div className="revlist-brand__text">
            <strong>Minhas receitas</strong>
            <span>Financeiro • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="revlist-top-actions">
          <button
            className="revlist-btn revlist-btn--ghost revlist-btn--desktop"
            type="button"
            onClick={() => navigate("/finance")}
          >
            Voltar
          </button>

          <button
            className="revlist-btn revlist-btn--ghost revlist-btn--desktop"
            type="button"
            onClick={() => fetchRevenues({ silent: true })}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>

          <button
            className="revlist-btn revlist-btn--primary"
            type="button"
            onClick={goNew}
          >
            + Receita
          </button>
        </div>
      </header>

      <main className="revlist-main">
        <section className="revlist-hero">
          <div>
            <span className="revlist-kicker">Controle de entradas</span>
            <h1>Receitas</h1>
            <p>
              Acompanhe suas entradas financeiras, revise lançamentos e mantenha
              o histórico pronto para análises e relatórios.
            </p>
          </div>

          <aside className="revlist-hero-card">
            <div className="revlist-hero-card__icon">💰</div>
            <div>
              <small>Total registrado</small>
              <strong>{moneyBR(metrics.total)}</strong>
              <span>{metrics.count} lançamento(s) na sua base.</span>
            </div>
          </aside>
        </section>

        <section className="revlist-metrics">
          <article className="revlist-metric revlist-metric--primary">
            <span>Total</span>
            <strong>{moneyBR(metrics.total)}</strong>
            <small>Todas as receitas</small>
          </article>

          <article className="revlist-metric">
            <span>Mês atual</span>
            <strong>{moneyBR(metrics.monthTotal)}</strong>
            <small>Entradas do período</small>
          </article>

          <article className="revlist-metric">
            <span>Média</span>
            <strong>{moneyBR(metrics.avg)}</strong>
            <small>Ticket médio</small>
          </article>

          <article className="revlist-metric">
            <span>Maior receita</span>
            <strong>{moneyBR(getRevenueValue(metrics.highest))}</strong>
            <small>
              {metrics.highest
                ? `${getRevenueCategory(metrics.highest)} • ${formatDateBR(
                    getRevenueDate(metrics.highest)
                  )}`
                : "Sem lançamentos"}
            </small>
          </article>
        </section>

        <section className="revlist-panel">
          <div className="revlist-panel__head">
            <div>
              <span>Lista financeira</span>
              <h2>Receitas cadastradas</h2>
              <p>
                {loading
                  ? "Carregando lançamentos..."
                  : `${filteredItems.length} de ${items.length} receita(s) exibida(s)`}
              </p>
            </div>

            <button
              className="revlist-btn revlist-btn--ghost-dark"
              type="button"
              onClick={() => fetchRevenues({ silent: true })}
              disabled={loading || refreshing}
            >
              {refreshing ? "Atualizando..." : "Atualizar"}
            </button>
          </div>

          <div className="revlist-toolbar">
            <div className="revlist-search">
              <span>⌕</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar categoria, descrição, valor ou data..."
                aria-label="Buscar receita"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Limpar busca"
                >
                  ×
                </button>
              ) : null}
            </div>

            <select
              className="revlist-select"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              aria-label="Ordenar receitas"
            >
              {SORT_OPTIONS.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {lastUpdatedAt ? (
            <div className="revlist-updated">
              Atualizado em {formatDateBR(lastUpdatedAt)} às{" "}
              {new Intl.DateTimeFormat("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              }).format(lastUpdatedAt)}
            </div>
          ) : null}

          {loading ? (
            <RevenueSkeleton />
          ) : filteredItems.length === 0 ? (
            <div className="revlist-state">
              <div className="revlist-state__icon">💰</div>

              <strong>
                {items.length === 0
                  ? "Nenhuma receita registrada."
                  : "Nenhuma receita encontrada."}
              </strong>

              <span>
                {items.length === 0
                  ? "Comece adicionando sua primeira entrada financeira."
                  : "Ajuste a busca ou limpe os filtros para ver todos os lançamentos."}
              </span>

              <div className="revlist-state__actions">
                {query ? (
                  <button
                    className="revlist-btn revlist-btn--ghost-dark"
                    type="button"
                    onClick={() => setQuery("")}
                  >
                    Limpar busca
                  </button>
                ) : null}

                <button
                  className="revlist-btn revlist-btn--primary"
                  type="button"
                  onClick={goNew}
                >
                  Nova receita
                </button>
              </div>
            </div>
          ) : (
            <div className="revlist-list">
              {filteredItems.map((item) => {
                const id = getRevenueId(item);
                const valor = getRevenueValue(item);
                const categoria = getRevenueCategory(item);
                const data = getRevenueDate(item);
                const descricao = getRevenueDescription(item);
                const meta = getCategoryMeta(categoria);

                return (
                  <article className="revlist-item" key={id}>
                    <div className={`revlist-icon revlist-icon--${meta.tone}`}>
                      {meta.icon}
                    </div>

                    <div className="revlist-main">
                      <div className="revlist-title-row">
                        <strong>{moneyBR(valor)}</strong>
                        <span>{categoria}</span>
                      </div>

                      <p>
                        {descricao
                          ? descricao
                          : "Sem descrição informada para esta receita."}
                      </p>

                      <small>{formatDateBR(data)}</small>
                    </div>

                    <div className="revlist-menu">
                      <button
                        className="revlist-link"
                        type="button"
                        onClick={() => goEdit(item)}
                      >
                        Editar
                      </button>

                      <button
                        className="revlist-link revlist-link--danger"
                        type="button"
                        onClick={() => askDelete(item)}
                      >
                        Excluir
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <button
        className="revlist-fab"
        type="button"
        onClick={goNew}
        aria-label="Nova receita"
      >
        +
      </button>

      <ConfirmModal
        open={confirmDel.open}
        revenue={confirmDel.revenue}
        deleting={deleting}
        onClose={closeDeleteModal}
        onConfirm={doDelete}
      />
    </div>
  );
}
